// client/src/pages/ClientFormPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import * as clientsApi from '../api/clients'; // Our frontend clients API service

function ClientFormPage() {
  const { token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams(); // Get client ID from URL params (if editing)
  const isEditing = !!id; // Boolean to check if we are in edit mode

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state for initial fetch/submission

  // Fetch client data if in edit mode
  useEffect(() => {
    const fetchClientData = async () => {
      if (authLoading) return; // Wait for auth to load

      if (isEditing) {
        try {
          const response = await clientsApi.fetchClientById(id);
          setFormData({
            name: response.client.name,
            email: response.client.email || '',
            phone: response.client.phone || '',
            address: response.client.address || '',
          });
          setLoading(false);
        } catch (err) {
          console.error('Failed to fetch client for editing:', err);
          setError(err.message || 'Failed to load client for editing.');
          setLoading(false);
        }
      } else {
        setLoading(false); // Not editing, so not loading data
      }
    };

    fetchClientData();
  }, [id, isEditing, token, authLoading]); // Dependency array: re-run when ID, mode, token, or authLoading changes

  // Handle changes for form fields
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle form submission (Create or Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      if (isEditing) {
        await clientsApi.updateClient(id, formData);
        setSuccess('Client updated successfully!');
      } else {
        await clientsApi.createClient(formData);
        setSuccess('Client added successfully!');
        navigate('/clients'); // Redirect to client list after creation
      }
    } catch (err) {
      console.error('Submission failed:', err);
      console.error('Full error details:', JSON.stringify(err, null, 2)); // Log detailed error information
      setError(err.message || 'Failed to save client. Please check the browser console for more details.');
    } finally {
      setLoading(false);
    }
  };

  // Display loading state
  if (authLoading || loading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-gray-50 p-4">
        <div className="text-xl text-gray-700">Loading Client Form...</div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-lg border border-gray-200">
        <h2 className="text-3xl font-bold text-primary-700 mb-6 text-center">
          {isEditing ? 'Edit Client' : 'Add New Client'}
        </h2>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md relative mb-4" role="alert">
            <span className="block sm:inline">{success}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., John Doe"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              required
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email (Optional)</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="client@example.com"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="555-123-4567"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">Address (Optional)</label>
            <textarea
              id="address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows="3"
              placeholder="123 Client St, City, State, Zip"
              className="mt-1 block w-full p-3 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            ></textarea>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-6 border-t border-gray-200">
            <Link
              to="/clients"
              className="w-full sm:w-auto text-center px-6 py-3 bg-gray-200 text-gray-800 font-medium rounded-lg shadow-md hover:bg-gray-300 transition duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 focus:ring-opacity-75"
            >
              Cancel
            </Link>
            <button
              type="submit"
              className="w-full sm:w-auto flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition duration-300"
              disabled={loading || authLoading}
            >
              {loading || authLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                isEditing ? 'Save Changes' : 'Add Client'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ClientFormPage;
